import { useEffect, useRef, useState } from 'react';

/** `rotuloCurto` vai no eixo; o `rotulo` inteiro aparece ao passar o mouse. */
export type PontoDoGrafico = { rotulo: string; rotuloCurto?: string; valor: number | null };

/**
 * Gráfico de linha simples, desenhado à mão em SVG: o painel de índices e a
 * evolução das rodadas precisam de uma linha, uma escala e o valor ao passar o
 * mouse — não justifica uma biblioteca de gráficos no pacote.
 *
 * A largura acompanha o contêiner para o texto não esticar junto com o SVG.
 */
export default function GraficoLinha({
  pontos,
  formatar,
  altura = 260,
  descricao,
  larguraMinima = 320,
}: {
  pontos: PontoDoGrafico[];
  formatar: (valor: number) => string;
  altura?: number;
  descricao: string;
  /** Abaixo disso o gráfico rola em vez de encolher; cartão estreito pede menos. */
  larguraMinima?: number;
}) {
  const caixa = useRef<HTMLDivElement>(null);
  const [largura, setLargura] = useState(720);
  const [foco, setFoco] = useState<number | null>(null);

  useEffect(() => {
    if (!caixa.current) return;
    const observador = new ResizeObserver(([entrada]) => setLargura(Math.max(larguraMinima, entrada.contentRect.width)));
    observador.observe(caixa.current);
    return () => observador.disconnect();
  }, [larguraMinima]);

  const validos = pontos
    .map((p, i) => ({ ...p, i }))
    .filter((p): p is PontoDoGrafico & { valor: number; i: number } => p.valor !== null);

  if (validos.length < 2) {
    return (
      <div ref={caixa} className="flex items-center justify-center text-[13px] text-muted" style={{ height: altura }}>
        Sem dados suficientes para o gráfico.
      </div>
    );
  }

  const margem = { topo: 18, direita: 18, base: 30, esquerda: 78 };
  const areaL = largura - margem.esquerda - margem.direita;
  const areaA = altura - margem.topo - margem.base;

  const valores = validos.map((p) => p.valor);
  const min = Math.min(...valores);
  const max = Math.max(...valores);
  const folga = (max - min) * 0.08 || max * 0.01 || 1;
  const piso = min - folga;
  const teto = max + folga;

  const x = (i: number) => margem.esquerda + (pontos.length === 1 ? areaL / 2 : (i / (pontos.length - 1)) * areaL);
  const y = (v: number) => margem.topo + (1 - (v - piso) / (teto - piso)) * areaA;

  const linha = validos.map((p, k) => `${k === 0 ? 'M' : 'L'}${x(p.i).toFixed(1)},${y(p.valor).toFixed(1)}`).join(' ');
  const area = `${linha} L${x(validos[validos.length - 1].i).toFixed(1)},${margem.topo + areaA} L${x(validos[0].i).toFixed(1)},${margem.topo + areaA} Z`;

  const marcasY = Array.from({ length: 5 }, (_, k) => piso + ((teto - piso) * k) / 4);
  // O espaçamento sai da largura do maior rótulo, para eles não se atropelarem.
  const eixo = (p: PontoDoGrafico) => p.rotuloCurto ?? p.rotulo;
  const larguraRotulo = Math.max(...pontos.map((p) => eixo(p).length)) * 6.6 + 20;
  const passoX = Math.max(1, Math.ceil(pontos.length / Math.max(2, Math.floor(areaL / larguraRotulo))));
  const ultimo = validos[validos.length - 1];
  const emFoco = foco !== null ? validos.find((p) => p.i === foco) : undefined;

  return (
    <div ref={caixa} className="relative w-full">
      <svg width={largura} height={altura} role="img" aria-label={descricao} className="block">
        <defs>
          <linearGradient id="grafico-area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--color-accent)" stopOpacity="0.18" />
            <stop offset="100%" stopColor="var(--color-accent)" stopOpacity="0" />
          </linearGradient>
        </defs>

        {marcasY.map((v) => (
          <g key={v}>
            <line x1={margem.esquerda} x2={largura - margem.direita} y1={y(v)} y2={y(v)} stroke="var(--color-border)" strokeDasharray="3 4" />
            <text x={margem.esquerda - 8} y={y(v)} textAnchor="end" dominantBaseline="middle" fontSize="11" fill="var(--color-muted)">
              {formatar(v)}
            </text>
          </g>
        ))}

        {pontos.map((p, i) =>
          (i % passoX === 0 && pontos.length - 1 - i >= passoX) || i === pontos.length - 1 ? (
            <text key={p.rotulo + i} x={x(i)} y={altura - 10} textAnchor={i === 0 ? 'start' : i === pontos.length - 1 ? 'end' : 'middle'} fontSize="11" fill="var(--color-muted)">
              {eixo(p)}
            </text>
          ) : null,
        )}

        <path d={area} fill="url(#grafico-area)" />
        <path d={linha} fill="none" stroke="var(--color-accent)" strokeWidth="2" strokeLinejoin="round" />

        <circle cx={x(ultimo.i)} cy={y(ultimo.valor)} r="4" fill="var(--color-accent)" stroke="var(--color-surface)" strokeWidth="2" />

        {emFoco && (
          <g>
            <line x1={x(emFoco.i)} x2={x(emFoco.i)} y1={margem.topo} y2={margem.topo + areaA} stroke="var(--color-ink-soft)" strokeWidth="1" />
            <circle cx={x(emFoco.i)} cy={y(emFoco.valor)} r="4.5" fill="var(--color-surface)" stroke="var(--color-accent)" strokeWidth="2" />
          </g>
        )}

        {validos.map((p) => (
          <rect
            key={p.i}
            x={x(p.i) - areaL / pontos.length / 2}
            y={margem.topo}
            width={Math.max(4, areaL / pontos.length)}
            height={areaA}
            fill="transparent"
            onMouseEnter={() => setFoco(p.i)}
            onMouseLeave={() => setFoco(null)}
          />
        ))}
      </svg>

      {emFoco && (
        <div
          className="pointer-events-none absolute z-10 -translate-x-1/2 rounded-md border border-border bg-surface px-sm py-xs text-[12px] shadow-md"
          style={{ left: Math.min(Math.max(x(emFoco.i), 90), largura - 90), top: Math.max(0, y(emFoco.valor) - 58) }}
        >
          <p className="text-muted">{emFoco.rotulo}</p>
          <p className="font-bold tabular-nums text-ink">{formatar(emFoco.valor)}</p>
        </div>
      )}
    </div>
  );
}
